import { makeAutoObservable } from "mobx";
import { IPlatformLocation, IReturnMethodAddress } from "../return-method-types";
import { IChangeable } from "../../../stores/types";

interface IReturnAddressChanges extends Partial<IReturnMethodAddress>{};

class ReturnAddressStore implements IChangeable<IReturnAddressChanges> {
    private _changes: IReturnAddressChanges = {};
    private _model?: IReturnMethodAddress|null;


    constructor() {
        makeAutoObservable(this);
	}

    public get deleted() { return this._changes._deleted ?? false };
    public get name() { return this._changes.name ?? this._model?.name };
    public get company() { return this._changes.company ?? this._model?.company };
    public get line1() { return this._changes.line_1 ?? this._model?.line_1 };
    public get line2() { return this._changes.line_2 ?? this._model?.line_2 };
    public get city() { return this._changes.city ?? this._model?.city };
    public get country() { return this._changes.country ?? this._model?.country };
    public get countryCode() { return this._changes.country_code ?? this._model?.country_code; }
    public get phone() { return this._changes.phone ?? this._model?.phone };
    public get postalCode() { return this._changes.postal_code ?? this._model?.postal_code };
    public get state() { return this._changes.state ?? this._model?.state };
    public get stateCode() { return this._changes.state_code ?? this._model?.state_code };
    public get locationId() {
        /** explicitly checking null b.c we set explicitly to null in custom return address */
        if (this._changes.shopify_location_id === null) {
            return null;
        }
        return this._changes.shopify_location_id ?? this._model?.shopify_location_id;
    };

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IReturnMethodAddress;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }


    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    public discardChanges(): void {
        this._changes = {} as IReturnAddressChanges;
    }

    public setModel(model: IReturnMethodAddress|undefined|null): void {
        this._model = model;
        this._changes = {} as IReturnAddressChanges;
    }

    public setDeleted(v: boolean) {
        this._changes._deleted = v;
    }

    public isEmpty(): boolean {
        const updatedModel = this.getUpdatedModel();
        if (!updatedModel) {
            return true;
        }
        return Object.keys(updatedModel).every(key => key.startsWith('_'));
    }

    public getUpdatedModel(): IReturnMethodAddress|undefined {
        const updatedModel = { ...this._model, ...this._changes, _has_changes: this.hasChanges() } as IReturnMethodAddress;
        const isNewAndDeleted = !(this._model ?? {}).id && this.deleted;
        if (isNewAndDeleted) {
            return undefined;
        }

        return updatedModel;
    }

    public setLocationId(v: string | null) :void {
        this._changes.shopify_location_id = v ;
        this._clearUnneededChanges();
    }

    public setName(v: string | null) :void {
        this._changes.name = v;
        this._clearUnneededChanges();
    }

    public setCompany(v: string | null) :void {
        this._changes.company = v;
        this._clearUnneededChanges();
    }

    public setLine1(v: string | null) :void {
        this._changes.line_1 = v;
        this._clearUnneededChanges();
    }

    public setLine2(v: string | null) :void  {
        this._changes.line_2 = v;
        this._clearUnneededChanges();
    }

    public setCity(v: string | null) :void  {
        this._changes.city = v;
        this._clearUnneededChanges();
    }

    public setCountry(v: string | null) :void  {
        this._changes.country = v;
        this._clearUnneededChanges();
    }

    public setCountryCode(v: string | null) :void  {
        this._changes.country_code = v;
        this._clearUnneededChanges();
    }

    public setPhone(v: string | null) :void  {
        this._changes.phone = v;
        this._clearUnneededChanges();
    }

    public setPostalCode(v: string | null) :void  {
        this._changes.postal_code = v;
        this._clearUnneededChanges();
    }

    public setState(v: string | null) :void  {
        this._changes.state = v;
        this._clearUnneededChanges();
    }

    public setStateCode(v: string | null) :void {
        this._changes.state_code = v;
        this._clearUnneededChanges();
    }

    public setSelectedAddress(address: IPlatformLocation | undefined) {
        if (!address) return;

        this.setName(address.name);
        this.setLine1(address.line_1);
        this.setLine2(address.line_2);
        this.setCity(address.city);
        this.setState(address.state);
        this.setStateCode(address.state_code);
        this.setPostalCode(address.postal_code);
        this.setPhone(address.phone);
        this.setCountry(address.country);
        this.setCountryCode(address.country_code);
        this.setLocationId(address.location_id);
    }
}

export default ReturnAddressStore;
